import {Component} from 'panel';
import isNumber from 'lodash/isNumber';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {avatarsStore} from 'common/report/stores/avatars';
import {getEntityCreatorDisplayName, userDetailsStore} from 'common/report/stores/user-details';
import {currentProjectStore} from 'common/report/stores/project-details';
import {Tooltip} from 'common/components/tooltip';
import {ICON_PROPERTIES_BY_SIZE, IconSizeType} from 'common/constants';
import {isStaffEmail} from 'common/report/util';

import {template} from './template';
import css from './index.styl?inline';
import mpAvatarUrl from './mp-avatar.svg';
import {AvatarType, track, TrackingEvent, TrackingEventProperty} from './tracking';

interface UserIconState {
  avatar: string;
  isAvatarImageLoaded: boolean;
}

const COLORS_TOTAL = 10;
export class UserIcon extends Component<UserIconState> {
  private avatarStoreCallback: () => void;
  private currentProjectStoreCallback: () => void;
  private userDetailsStoreCallback: () => void;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'img-src': {type: `string`, description: `link to the image`},
      name: `string`,
      id: {
        type: `number`,
        default: null,
      },
      'disable-tooltip': `boolean`,
      size: {type: `string`, enum: Object.values(IconSizeType)},
      'tooltip-text': `string`,
      'tooltip-placement': Tooltip.attrsSchema[`placement`],
      email: {type: `string`, description: `used to determine if staff user`},
      'include-label': {type: `boolean`, description: `show user name for the icon to the right`},
      'label-prefix': {type: `string`},
    };
  }
  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `id` || attr === `size`) {
      this.updateAvatarUrlFromStore();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.avatarStoreCallback = () => {
      this.updateAvatarUrlFromStore();
    };

    this.currentProjectStoreCallback = () => {
      this.fetchAvatars();
    };
    this.userDetailsStoreCallback = () => {
      this.update();
    };

    avatarsStore.subscribe(this.avatarStoreCallback);
    currentProjectStore.subscribe(this.currentProjectStoreCallback);
    userDetailsStore.subscribe(this.userDetailsStoreCallback);

    this.fetchAvatars();
  }

  disconnectedCallback() {
    avatarsStore.unsubscribe(this.avatarStoreCallback);
    currentProjectStore.unsubscribe(this.currentProjectStoreCallback);
    userDetailsStore.unsubscribe(this.userDetailsStoreCallback);
    super.disconnectedCallback();
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        avatar: null,
        isAvatarImageLoaded: false,
      },
      helpers: {
        getBackgroundColorClass: () => {
          const colorId = isNumber(this.attr(`id`)) ? this.attr(`id`) % COLORS_TOTAL : `default`;
          return `color-${colorId}`;
        },
        getIconProperties: () => {
          return ICON_PROPERTIES_BY_SIZE[this.attr(`size`)];
        },
        getAvatarUrl: () => {
          if (this.attr(`img-src`)) {
            return this.attr(`img-src`);
          } else if (this.shouldShowStaffAvatar()) {
            return mpAvatarUrl;
          }
          return this.state.avatar;
        },
        handleIconClick: () => {
          let avatarType = AvatarType.Empty;
          if (this.helpers.getAvatarUrl()) {
            avatarType = AvatarType.Photo;
          } else if (this.attr(`name`)) {
            avatarType = AvatarType.Name;
          }
          track(TrackingEvent.IconClick, {
            [TrackingEventProperty.AvatarType]: avatarType,
          });
        },
        handleAvatarImageLoaded: () => {
          this.update({isAvatarImageLoaded: true});
        },
        getDisplayName: () => {
          return getEntityCreatorDisplayName(this.attr(`email`), this.attr(`name`));
        },
      },
    };
  }

  updateAvatarUrlFromStore() {
    const uuid = avatarsStore.value?.avatars?.[this.attr(`id`)];
    const size = this.helpers.getIconProperties()?.imgSize;

    const avatar = uuid && size ? `${avatarsStore.value.base}/${uuid}/${size}x${size}.png` : null;
    this.update({avatar});
  }

  fetchAvatars() {
    const organizationId = currentProjectStore.value?.organizationId;
    organizationId && avatarsStore.fetch({organizationId});
  }

  shouldShowStaffAvatar() {
    return isStaffEmail(this.attr(`email`)) && !userDetailsStore.value.isStaff;
  }
}

export const UserIconEl = defineAndMakeJsxFactory(`mp-user-icon`, UserIcon);
